import React from "react";
import { Camera, Paperclip, RotateCw, Send, MessageSquareText, X } from "lucide-react";
import { pickLanguage, useLanguage, type Language } from "../ui/LanguageProvider";
import Spinner from "../ui/Spinner";

const MAX_FILES = 5;
// 첨부는 서명 URL로 Storage에 직접 올리므로 서버리스 본문 한도와 무관 — 버킷 한도(25MB)와 일치.
const MAX_BYTES = 25 * 1024 * 1024;
const ENDPOINT = "/api/feedback";

// 피드백 유형 — 서버(feedback-handler)의 FEEDBACK_TYPES와 동일하게 유지. key는 CSS 클래스용.
const FEEDBACK_TYPES = [
  { value: "오류", key: "bug", emoji: "🐞", en: "Bug" },
  { value: "개선요청", key: "improve", emoji: "🛠️", en: "Improvement" },
  { value: "아이디어", key: "idea", emoji: "💡", en: "Idea" },
  { value: "기타", key: "etc", emoji: "💬", en: "Other" },
] as const;
type FeedbackType = (typeof FEEDBACK_TYPES)[number]["value"];

type ChatMessage = {
  id: number;
  role: "user" | "system";
  text: string;
  error?: boolean;
};

type Attachment = {
  id: number;
  file: File;
  previewUrl: string | null; // 이미지면 썸네일 미리보기
};

type Payload = {
  message: string;
  type: FeedbackType;
  pageUrl: string;
  attachments: { name: string; path: string }[];
};

// 파일 바이트는 서버리스 함수 본문(배포 시 4.5MB 한도)을 거치지 않도록
// 서명 업로드 URL을 받아 Supabase Storage로 직접 PUT 한다.
async function uploadFileToStorage(file: File, language: Language = "ko"): Promise<{ name: string; path: string }> {
  const urlResponse = await fetch(`${ENDPOINT}/upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ filename: file.name, contentType: file.type || "application/octet-stream" }),
  });
  const urlJson = await urlResponse.json().catch(() => ({}));
  if (!urlResponse.ok || !urlJson.ok) {
    throw new Error(urlJson.error || pickLanguage(language, `업로드 URL 발급 실패 (HTTP ${urlResponse.status})`, `Failed to get upload URL (HTTP ${urlResponse.status})`));
  }

  const putResponse = await fetch(urlJson.url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!putResponse.ok) {
    const detail = await putResponse.text().catch(() => "");
    const tail = `(HTTP ${putResponse.status}${detail ? `: ${detail.slice(0, 120)}` : ""})`;
    throw new Error(pickLanguage(language, `"${file.name}" 업로드 실패 ${tail}`, `Failed to upload "${file.name}" ${tail}`));
  }
  return { name: file.name, path: urlJson.path };
}

// 현재 탭 화면을 getDisplayMedia로 캡처해 PNG File로 반환. 사용자가 취소하면 null.
async function captureScreen(language: Language = "ko"): Promise<File | null> {
  const constraints: MediaStreamConstraints & { preferCurrentTab?: boolean } = {
    video: true,
    audio: false,
    preferCurrentTab: true, // Chrome: 현재 탭을 기본 선택지로
  };
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia(constraints);
  } catch {
    return null; // 사용자가 공유를 취소한 경우
  }
  try {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((resolve) => setTimeout(resolve, 250)); // 첫 프레임 안정화
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) throw new Error(pickLanguage(language, "캡처용 캔버스를 만들 수 없습니다.", "Couldn't create a capture canvas."));
    context.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error(pickLanguage(language, "화면 캡처 이미지 생성에 실패했습니다.", "Failed to create the screen capture image."));
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    return new File([blob], `capture-${stamp}.png`, { type: "image/png" });
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

function formatSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

function endpointHint(error: string, language: Language = "ko") {
  if (/API route not found|404|Not Found/i.test(error)) {
    return pickLanguage(
      language,
      "API route not found: 배포 환경에 /api/feedback 라우트가 아직 반영되지 않았거나, 로컬에서 npm run dev:local 대신 CRA dev server만 실행 중일 수 있습니다.",
      "API route not found: the /api/feedback route may not be deployed yet, or locally only the CRA dev server is running instead of npm run dev:local."
    );
  }
  return error;
}

export default function FeedbackWidget() {
  const { language, t } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const welcomeText = t(
    "사용 중 불편한 점이나 개선 아이디어를 남겨주세요. 스크린샷 등 파일 첨부도 가능합니다. (최대 5개 · 각 25MB)",
    "Tell us about any issues or ideas for improvement. You can attach files such as screenshots. (Up to 5 · 25MB each)"
  );
  const [messages, setMessages] = React.useState<ChatMessage[]>([
    {
      id: 0,
      role: "system",
      text: "사용 중 불편한 점이나 개선 아이디어를 남겨주세요. 스크린샷 등 파일 첨부도 가능합니다. (최대 5개 · 각 25MB)",
    },
  ]);
  const [draft, setDraft] = React.useState("");
  const [feedbackType, setFeedbackType] = React.useState<FeedbackType>("오류");
  const [attachments, setAttachments] = React.useState<Attachment[]>([]);
  const [sending, setSending] = React.useState(false);
  const [uploading, setUploading] = React.useState<string | null>(null);
  const [capturing, setCapturing] = React.useState(false);
  const lastFailedRef = React.useRef<Payload | null>(null);
  const nextIdRef = React.useRef(1);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const pushMessage = React.useCallback((message: Omit<ChatMessage, "id">) => {
    setMessages((prev) => [...prev, { ...message, id: nextIdRef.current++ }]);
  }, []);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, attachments.length]);

  // 언마운트 시 썸네일 object URL 정리
  React.useEffect(() => {
    return () => {
      setAttachments((prev) => {
        prev.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
        return prev;
      });
    };
  }, []);

  // 주의: 검증/추가 계산은 setState 업데이터 밖에서 — 업데이터 안에서 push하면
  // StrictMode 이중 실행으로 같은 파일이 두 번 첨부된다.
  const attachmentsRef = React.useRef<Attachment[]>([]);
  attachmentsRef.current = attachments;

  const addFiles = (incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const current = attachmentsRef.current;
    const rejected: string[] = [];
    const accepted: Attachment[] = [];

    for (const file of Array.from(incoming)) {
      if (current.length + accepted.length >= MAX_FILES) {
        rejected.push(t(`첨부는 최대 ${MAX_FILES}개까지 가능합니다. "${file.name}"은(는) 제외했어요.`, `You can attach up to ${MAX_FILES} files. "${file.name}" was skipped.`));
        continue;
      }
      if (file.size > MAX_BYTES) {
        rejected.push(t(`"${file.name}" (${formatSize(file.size)}) 파일이 너무 큽니다. 최대 25MB까지 가능합니다.`, `"${file.name}" (${formatSize(file.size)}) is too large. The limit is 25MB.`));
        continue;
      }
      const isDuplicate = [...current, ...accepted].some(
        (item) => item.file.name === file.name && item.file.size === file.size
      );
      if (isDuplicate) continue;
      accepted.push({
        id: nextIdRef.current++,
        file,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      });
    }

    if (accepted.length) setAttachments([...current, ...accepted]);
    // 거부 사유는 놓치지 않게 채팅 버블로 안내
    rejected.forEach((reason) => pushMessage({ role: "system", text: reason, error: true }));
  };

  const removeAttachment = (id: number) => {
    setAttachments((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const clearAttachments = () => {
    setAttachments((prev) => {
      prev.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  };

  const submitPayload = async (payload: Payload) => {
    setSending(true);
    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json.ok) throw new Error(json.error || `HTTP ${response.status}`);
      lastFailedRef.current = null;
      const attachInfo = payload.attachments.length ? `첨부 ${payload.attachments.length}건과 함께 ` : "";
      const linearInfo = json.linear?.identifier ? ` Linear ${json.linear.identifier}로 등록됐어요.` : "";
      const attachInfoEn = payload.attachments.length ? ` with ${payload.attachments.length} attachment${payload.attachments.length === 1 ? "" : "s"}` : "";
      const linearInfoEn = json.linear?.identifier ? ` Filed as Linear ${json.linear.identifier}.` : "";
      pushMessage({
        role: "system",
        text: t(`접수됐습니다. ${attachInfo}Slack 채널로 전달했어요.${linearInfo}`, `Received. Sent to Slack${attachInfoEn}.${linearInfoEn}`),
      });
    } catch (error) {
      lastFailedRef.current = payload;
      const message = endpointHint(error instanceof Error ? error.message : t("네트워크 오류", "Network error"), language);
      pushMessage({ role: "system", text: t(`전송에 실패했어요: ${message}`, `Failed to send: ${message}`), error: true });
    } finally {
      setSending(false);
    }
  };

  // 화면 캡처 — 캡처 화면에 패널이 찍히지 않게 잠시 숨기고 진행.
  const handleCapture = async () => {
    if (capturing || sending) return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      pushMessage({ role: "system", text: t("이 브라우저는 화면 캡처를 지원하지 않습니다. 파일 첨부를 이용해주세요.", "This browser doesn't support screen capture. Please attach a file instead."), error: true });
      return;
    }
    setCapturing(true);
    try {
      const captured = await captureScreen(language);
      if (captured) addFiles([captured]);
    } catch (error) {
      pushMessage({
        role: "system",
        text: error instanceof Error ? error.message : t("화면 캡처에 실패했습니다.", "Screen capture failed."),
        error: true,
      });
    } finally {
      setCapturing(false);
    }
  };

  const handleSend = async () => {
    const message = draft.trim();
    if (!message || sending || uploading) return;

    // 1) 첨부를 Storage로 직접 업로드(서명 URL) — 실패해도 내용·첨부는 그대로 유지된다.
    const uploaded: Payload["attachments"] = [];
    try {
      for (let index = 0; index < attachments.length; index += 1) {
        setUploading(t(`첨부 업로드 중... (${index + 1}/${attachments.length}) ${attachments[index].file.name}`, `Uploading attachments... (${index + 1}/${attachments.length}) ${attachments[index].file.name}`));
        uploaded.push(await uploadFileToStorage(attachments[index].file, language));
      }
    } catch (error) {
      setUploading(null);
      const reason = error instanceof Error ? error.message : t("첨부 업로드에 실패했습니다.", "Failed to upload attachments.");
      pushMessage({
        role: "system",
        text: t(`첨부 업로드에 실패했어요: ${reason}\n입력한 내용과 첨부는 그대로 남아 있으니 전송을 다시 눌러주세요.`, `Failed to upload attachments: ${reason}\nYour message and attachments are kept — click Send again.`),
        error: true,
      });
      return;
    }
    setUploading(null);

    // 2) 본문 + 업로드된 경로만 전송(소용량 JSON).
    const typeItem = FEEDBACK_TYPES.find((item) => item.value === feedbackType);
    const typeEmoji = typeItem?.emoji || "💬";
    const typeLabel = language === "en" && typeItem ? typeItem.en : feedbackType;
    pushMessage({
      role: "user",
      text: [
        `${typeEmoji} [${typeLabel}] ${message}`,
        ...attachments.map((item) => `📎 ${item.file.name} (${formatSize(item.file.size)})`),
      ].join("\n"),
    });
    setDraft("");
    clearAttachments();
    await submitPayload({ message, type: feedbackType, pageUrl: window.location.href, attachments: uploaded });
  };

  return (
    <div className="feedback-nav">
      <button
        type="button"
        className={`top-nav-action feedback-nav__trigger${open ? " is-active" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? (language === "en" ? "Close feedback" : "피드백 닫기") : (language === "en" ? "Send feedback" : "피드백 보내기")}
        title={language === "en" ? "Send feedback" : "피드백 보내기"}
      >
        {open ? <X size={16} /> : <MessageSquareText size={16} />}
        <span className="feedback-nav__triggerText">{language === "en" ? "Feedback" : "피드백"}</span>
      </button>

      {open ? (
        <div
          className="feedback-nav__panel"
          role="dialog"
          aria-label={t("피드백 보내기", "Send feedback")}
          style={capturing ? { visibility: "hidden" } : undefined}
        >
          <div className="feedback-nav__header">
            <div>
              <strong>{t("피드백 보내기", "Send feedback")}</strong>
              <span>{t("오류, 개선 요청, 아이디어를 Slack으로 전달합니다.", "Bugs, improvement requests and ideas are sent to Slack.")}</span>
            </div>
            <button type="button" className="feedback-nav__close" onClick={() => setOpen(false)} aria-label={t("닫기", "Close")}>
              <X size={16} />
            </button>
          </div>

          <div ref={scrollRef} className="feedback-nav__messages">
            {messages.map((message) => (
              <div key={message.id} className={`feedback-nav__bubbleRow feedback-nav__bubbleRow--${message.role}`}>
                <div className={`feedback-nav__bubble${message.error ? " feedback-nav__bubble--error" : ""}`}>
                  {message.id === 0 ? welcomeText : message.text}
                  {message.error && lastFailedRef.current ? (
                    <button
                      type="button"
                      className="feedback-nav__retry"
                      onClick={() => lastFailedRef.current && submitPayload(lastFailedRef.current)}
                      disabled={sending}
                      aria-busy={sending || undefined}
                    >
                      {sending ? <Spinner size={12} /> : <RotateCw size={12} />}
                      {t("재시도", "Retry")}
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            {uploading ? <div className="feedback-nav__sending">{uploading}</div> : null}
            {sending ? <div className="feedback-nav__sending">{t("전송 중...", "Sending...")}</div> : null}
          </div>

          {attachments.length > 0 ? (
            <div className="feedback-nav__attachments">
              <div className="feedback-nav__attachTitle">{t(`보낼 첨부 ${attachments.length}건`, `${attachments.length} attachment${attachments.length === 1 ? "" : "s"} to send`)}</div>
              {attachments.map((item) => (
                <div key={item.id} className="feedback-nav__attachCard">
                  {item.previewUrl ? (
                    <img className="feedback-nav__thumb" src={item.previewUrl} alt={item.file.name} />
                  ) : (
                    <span className="feedback-nav__thumb feedback-nav__thumb--file">
                      <Paperclip size={14} />
                    </span>
                  )}
                  <span className="feedback-nav__attachMeta">
                    <span className="feedback-nav__attachName">{item.file.name}</span>
                    <span className="feedback-nav__attachSize">{formatSize(item.file.size)}</span>
                  </span>
                  <button type="button" onClick={() => removeAttachment(item.id)} aria-label={t(`${item.file.name} 제거`, `Remove ${item.file.name}`)}>
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="feedback-nav__types" role="radiogroup" aria-label={t("피드백 유형", "Feedback type")}>
            <span className="feedback-nav__typesLabel">{t("유형", "Type")}</span>
            {FEEDBACK_TYPES.map((item) => (
              <button
                key={item.value}
                type="button"
                role="radio"
                aria-checked={feedbackType === item.value}
                className={`feedback-nav__type feedback-nav__type--${item.key}${feedbackType === item.value ? " is-active" : ""}`}
                onClick={() => setFeedbackType(item.value)}
              >
                {item.emoji} {language === "en" ? item.en : item.value}
              </button>
            ))}
          </div>

          <div className="feedback-nav__composer">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <button type="button" className="feedback-nav__tool" onClick={() => fileInputRef.current?.click()} aria-label={t("파일 첨부", "Attach file")} title={t("파일 첨부", "Attach file")}>
              <Paperclip size={16} />
            </button>
            <button
              type="button"
              className="feedback-nav__tool"
              onClick={() => void handleCapture()}
              disabled={capturing}
              aria-busy={capturing || undefined}
              aria-label={t("현재 화면 캡처", "Capture current screen")}
              title={t("현재 화면 캡처", "Capture current screen")}
            >
              {capturing ? <Spinner size={16} /> : <Camera size={16} />}
            </button>
            <textarea
              value={draft}
              rows={2}
              placeholder={t("내용 입력", "Enter your message")}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
            />
            <button type="button" className="feedback-nav__send" onClick={() => void handleSend()} disabled={sending || !!uploading || !draft.trim()} aria-busy={sending || !!uploading || undefined} aria-label={sending || uploading ? t("보내는 중…", "Sending…") : t("전송", "Send")}>
              {sending || uploading ? <Spinner size={15} /> : <Send size={15} />}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
